import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Alert, AlertDescription } from "../ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Spinner } from "../ui/spinner";
import { SubCategoryService } from "../../services/subCategoryService";

const EditSubCategoryForm = ({
  open,
  onClose,
  subCategory,
  categories,
  onSubCategoryUpdated,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    id_prefix: "",
    category_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (subCategory && open) {
      setFormData({
        name: subCategory.name || "",
        description: subCategory.description || "",
        id_prefix: subCategory.id_prefix || "",
        category_id: subCategory.category_id || "",
      });
      setError(null);
    }
  }, [subCategory, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subCategory) return;

    try {
      setLoading(true);
      setError(null);

      const updateData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        id_prefix: formData.id_prefix.trim(),
        category_id: formData.category_id,
      };

      await SubCategoryService.updateSubCategory(
        subCategory.id,
        updateData,
        getAccessTokenSilently
      );

      onSubCategoryUpdated();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      category_id: value,
    }));
  };

  const isValid =
    formData.name.trim() && formData.id_prefix.trim() && formData.category_id;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Sub-Category</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Sub-Category Name</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter sub-category name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category_id">Parent Category</Label>
            <Select
              value={formData.category_id}
              onValueChange={handleSelectChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select parent category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name} ({category.id_prefix})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="id_prefix">ID Prefix</Label>
            <Input
              id="id_prefix"
              name="id_prefix"
              value={formData.id_prefix}
              onChange={handleInputChange}
              placeholder="Enter ID prefix (e.g., NS, DP, AM)"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter sub-category description"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || loading}>
              {loading && <Spinner size="sm" className="mr-2" />}
              Update Sub-Category
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditSubCategoryForm;
